import React from 'react';
import { TAB_TYPES, slugify } from './tabTypes';
import TabPropertiesEditor from './TabPropertiesEditor';

const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white';
const sectionTitleClass = 'text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3';

const Section = ({ title, children }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
    <h4 className={sectionTitleClass}>{title}</h4>
    {children}
  </div>
);

// Colonne droite : édition détaillée de l'onglet sélectionné
const TabEditorPanel = ({ tab, index, onUpdate, onRemove }) => {
  if (!tab) {
    return (
      <div className="flex items-center justify-center h-full text-center text-gray-400 dark:text-gray-500">
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <p className="text-sm">Sélectionnez un onglet à gauche pour l'éditer</p>
        </div>
      </div>
    );
  }

  const patch = (changes) => onUpdate(index, { ...tab, ...changes });

  const handleNameChange = (e) => {
    const newName = e.target.value;
    // Garder le slug synchronisé tant qu'il n'a pas été personnalisé
    const newSlug = tab.slug === slugify(tab.name) ? slugify(newName) : tab.slug;
    patch({ name: newName, slug: newSlug });
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    let properties;
    try {
      properties = structuredClone(TAB_TYPES[newType].defaultProperties);
    } catch (err) {
      properties = JSON.parse(JSON.stringify(TAB_TYPES[newType].defaultProperties));
    }
    patch({ type: newType, properties });
  };

  const handlePropertyChange = (propertyName, value) => {
    patch({ properties: { ...tab.properties, [propertyName]: value } });
  };

  const convertToWeekly = (clientSlug) => {
    patch({ type: 'weekly', properties: { clientSlug } });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 flex-1 pr-4">
          {TAB_TYPES[tab.type]?.description}
        </p>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="flex-shrink-0 inline-flex items-center px-2.5 py-1.5 text-sm text-red-600 hover:text-white hover:bg-red-600 border border-red-200 dark:border-red-800 rounded-md transition-colors"
          title="Supprimer cet onglet"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Supprimer
        </button>
      </div>

      <div className="space-y-4 overflow-y-auto pr-1">
        {/* Général */}
        <Section title="Général">
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Nom de l'onglet</label>
              <input
                type="text"
                value={tab.name}
                onChange={handleNameChange}
                className={inputClass}
                placeholder="Nom de l'onglet"
              />
            </div>

            <div>
              <label className={labelClass}>Type</label>
              <select value={tab.type} onChange={handleTypeChange} className={inputClass}>
                {Object.entries(TAB_TYPES).map(([value, { label }]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Slug (identifiant pour l'URL)</label>
              <div className="flex">
                <input
                  type="text"
                  value={tab.slug || ''}
                  onChange={(e) => patch({ slug: slugify(e.target.value) })}
                  placeholder="slug-de-l-onglet"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => patch({ slug: slugify(tab.name) })}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md hover:bg-gray-200 dark:hover:bg-gray-500"
                  title="Générer depuis le nom"
                >
                  Générer
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Utilisé dans l'URL pour accéder directement à cet onglet.
              </p>
            </div>
          </div>
        </Section>

        {/* Visibilité & groupe */}
        <Section title="Visibilité & groupe">
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`admin-only-${index}`}
                checked={tab.admin_only || false}
                onChange={(e) => patch({ admin_only: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded dark:border-gray-600 dark:bg-gray-700"
              />
              <label htmlFor={`admin-only-${index}`} className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Visible uniquement par les administrateurs
              </label>
            </div>

            <div>
              <label className={labelClass}>Groupe</label>
              <input
                type="text"
                value={tab.group || ''}
                onChange={(e) => patch({ group: e.target.value.trim() || null })}
                className={inputClass}
                placeholder="Nom du groupe (optionnel)"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Les onglets partageant le même groupe sont rassemblés dans un menu déroulant. Laissez vide pour un regroupement automatique par premier mot.
              </p>
            </div>
          </div>
        </Section>

        {/* Propriétés spécifiques au type */}
        <Section title="Propriétés">
          <TabPropertiesEditor
            tab={tab}
            onPropertyChange={handlePropertyChange}
            onConvertToWeekly={convertToWeekly}
          />
        </Section>
      </div>
    </div>
  );
};

export default TabEditorPanel;
